import React, { useState, useEffect } from "react";
import API from "../../Network/API";
import { toast } from "react-toastify";

export default function MeetingCard({ meeting, currentUserId, onStart, onPreview, onShare, onEdit }) {
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  const [isOverdue, setIsOverdue] = useState(false);
  const [isEditingRsvp, setIsEditingRsvp] = useState(false);
  const [currentRsvp, setCurrentRsvp] = useState(meeting.rsvp_status); // Maps to 1 (accept), 2 (maybe), 3 (reject)

  useEffect(() => {
    // Initialize RSVP from JSON since API returns {"rsvp_status": 1} inside meeting wrapper for the current user
    setCurrentRsvp(meeting.rsvp_status);
  }, [meeting.rsvp_status]);

  useEffect(() => {
    const calculateCountdown = () => {
      // The start_date string comes from backend as e.g. "2026-04-03 18:30:00" in local timezone
      // However parsing that in a browser depends on system TZ. 
      // The most resilient fallback is to parse Date.parse(meeting.start_date.replace(" ", "T"))
      const startDateTime = new Date(meeting.start_date.replace(' ', 'T'));
      const now = new Date();
      const diffMs = startDateTime - now;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins <= 0) {
        setIsOverdue(true);
        setRemainingMinutes(Math.max(0, diffMins)); 
      } else {
        setIsOverdue(false);
        setRemainingMinutes(diffMins);
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 10000); // 10 sec heartbeat
    return () => clearInterval(interval);
  }, [meeting.start_date]);

  const updateRsvp = (statusMapValue) => {
    // 1=accept, 2=maybe, 3=reject
    setCurrentRsvp(statusMapValue);
    setIsEditingRsvp(false);
    
    let stringLabel = '';
    if (statusMapValue === 1) stringLabel = 'accept';
    if (statusMapValue === 2) stringLabel = 'maybe';
    if (statusMapValue === 3) stringLabel = 'reject';

    toast.info("Updating RSVP...");
    API.post("/self-meeting-status-modify", {
      meeting_room_id: meeting.id,
      status: stringLabel
    }).then(res => {
      if (res.data.success) {
        toast.success(`RSVP updated to ${stringLabel.toUpperCase()}`);
      } else {
        toast.error(res.data.message || "Failed to update RSVP");
      }
    }).catch(err => {
      const msg = err?.response?.data?.message || err?.message || "Failed to update RSVP";
      toast.error(msg);
      console.error("RSVP Error Payload:", err?.response?.data);
    });
  };

  // UI Tokens mimicking Flutter
  const kGreen = "#10B981";
  const kAmber = "#F59E0B";
  const kAmberDeep = "#D97706";
  const kRed = "#EF4444";
  const kText = "#1F2937";
  const kTextDim = "#6B7280";

  const isOwner = meeting.is_owner === 1;

  let urgencyColor = kAmberDeep;
  if (isOverdue) urgencyColor = kGreen;
  else if (remainingMinutes !== null && remainingMinutes <= 5) urgencyColor = kRed;
  else if (remainingMinutes !== null && remainingMinutes <= 30) urgencyColor = kAmberDeep;

  let stripGradient = isOverdue 
    ? "linear-gradient(to right, #059669, #10B981)" 
    : "linear-gradient(to right, #F59E0B, #EA580C)";

  const formatCountdownText = () => {
    if (isOverdue) return "Meeting Now!";
    if (remainingMinutes === null) return "Loading...";
    const hrs = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  // RSVP Engine UI
  const formatRsvpBadge = () => {
    if (isOwner) return null; // Host sees no RSVP
    const hasResponded = [1, 2, 3].includes(currentRsvp);

    if (hasResponded && !isEditingRsvp) {
      let label = "Accept";
      let badgeColor = kGreen;
      if (currentRsvp === 2) { label = "Maybe"; badgeColor = kAmber; }
      if (currentRsvp === 3) { label = "Decline"; badgeColor = kRed; }

      return (
        <div className="no-card-click" style={{ flex: 1, paddingRight: "8px", alignSelf: "center" }}>
          <div 
            onClick={() => setIsEditingRsvp(true)}
            style={{
              padding: "7px 10px",
              backgroundColor: `${badgeColor}15`, // soft tint
              borderRadius: "10px",
              border: `1px solid ${badgeColor}33`,
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <i className="fa-regular fa-circle-check" style={{ fontSize: "14px", color: badgeColor }}></i>
            <span style={{ fontSize: "11px", fontWeight: "700", color: badgeColor, margin: "0 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {label}
            </span>
            <i className="fa-solid fa-pen" style={{ fontSize: "10px", color: "#6c757d" }}></i>
          </div>
        </div>
      );
    }

    // Unresponded or Editing mode -> Expand 3 buttons
    return (
      <div className="no-card-click" style={{ flex: 1, paddingRight: "8px", display: "flex", gap: "6px", alignSelf: "center" }}>
        {[
          { id: 1, label: "Accept", hex: kGreen, soft: "#ECFDF5" },
          { id: 2, label: "Maybe", hex: kAmber, soft: "#FFFBEB" },
          { id: 3, label: "Decline", hex: kRed, soft: "#FEF2F2" },
        ].map(opt => {
          const isSelected = currentRsvp === opt.id;
          return (
            <div 
              key={opt.id}
              onClick={() => updateRsvp(opt.id)}
              style={{
                flex: 1,
                padding: "8px 0",
                textAlign: "center",
                borderRadius: "10px",
                backgroundColor: isSelected ? opt.hex : opt.soft,
                border: `1.5px solid ${isSelected ? opt.hex : opt.hex + '4D'}`,
                color: isSelected ? "#fff" : opt.hex,
                fontSize: "11px",
                fontWeight: isSelected ? "700" : "500",
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {opt.label}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="col">
      <div 
        className="card shadow-sm border-0" 
        style={{ 
          borderRadius: "20px", 
          backgroundColor: "#FFFCF5", 
          overflow: "hidden",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.className = "card shadow border-0";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.className = "card shadow-sm border-0";
        }}
        onClick={(e) => {
           // Prevent opening details if clicking an inline button, menu, or RSVP token
           if (e.target.closest('.no-card-click')) return;
           window.location.href = `/meeting-details/${meeting.room_name}`;
        }}
      >
        {/* Top Strip */}
        <div style={{ height: "3px", background: stripGradient }}></div>

        <div className="card-body p-3 p-md-4">
          {/* Header Row: Icon, Title & Menu */}
          <div className="d-flex align-items-start mb-3">
            <div 
              className="d-flex align-items-center justify-content-center" 
              style={{
                width: "40px", 
                height: "40px",
                borderRadius: "12px",
                background: isOverdue ? `${kGreen}1A` : `${kAmber}1A`, // 0.1 opacity roughly
                border: `1px solid ${isOverdue ? kGreen : kAmber}4D`,
                marginRight: "12px",
                flexShrink: 0
              }}
            >
              <i className={isOverdue ? "fa-solid fa-video" : "fa-regular fa-calendar"} style={{ color: isOverdue ? kGreen : kAmberDeep, fontSize: "16px" }}></i>
            </div>

            <div className="flex-grow-1 min-w-0">
              <div 
                className="mb-1"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "3px 8px",
                  borderRadius: "20px",
                  background: stripGradient,
                  color: "#fff",
                  fontSize: "9px",
                  fontWeight: "800",
                  letterSpacing: "0.5px"
                }}
              >
                <i className={isOverdue ? "fa-solid fa-video me-1" : "fa-regular fa-calendar me-1"} style={{ fontSize: "8px" }}></i>
                {isOverdue ? "STARTING NOW" : "SCHEDULED"}
              </div>
              <h6 className="mb-0 text-truncate" style={{ fontSize: "15px", fontWeight: "700", color: kText }}>
                {meeting.room_title || "Meeting"}
              </h6>
            </div>

            {/* Overflow Dropdown */}
            <div className="dropdown ms-2 flex-shrink-0 no-card-click">
              <button className="btn btn-sm btn-link text-decoration-none text-muted py-0 px-2" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="fa-solid fa-ellipsis-vertical"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                <li><button className="dropdown-item" onClick={() => onStart(meeting.room_name)}>Start / Join</button></li>
                <li><a className="dropdown-item" href={`/meeting-waiting/${meeting.room_name}`}>Preview</a></li>
                <li><button className="dropdown-item" onClick={() => onShare(meeting)}>Share</button></li>
                {isOwner && <li><button className="dropdown-item" onClick={() => onEdit(meeting)}>Edit</button></li>}
              </ul>
            </div>
          </div>

          {/* Time & Countdown Box */}
          <div 
            className="d-flex rounded p-2 px-3 mb-2"
            style={{
              backgroundColor: isOverdue ? `${kGreen}0F` : `${kAmber}0F`,
              border: `1px solid ${isOverdue ? kGreen : kAmber}26`
            }}
          >
            <div className="flex-grow-1 border-end me-3 pe-3 border-opacity-25" style={{ borderColor: isOverdue ? kGreen : kAmber }}>
              <div className="d-flex align-items-center mb-1">
                <i className="fa-regular fa-clock me-1" style={{ fontSize: "10px", color: "#B45309" }}></i>
                <span style={{ fontSize: "10px", color: kTextDim, fontWeight: "500" }}>Starts at</span>
              </div>
              <div style={{ fontSize: "14px", fontWeight: "800", color: kText, letterSpacing: "-0.3px" }}>
                {/* Format identical to Flutter recent patch: EEEE, dd MMMM - 6:30pm Europe/London Time */}
                {new Date(meeting.start_date.replace(' ', 'T')).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long'})}
                <span className="fw-semibold"> - {meeting.start_time_text || (new Date(meeting.start_date.replace(' ', 'T')).toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' }) + ' ' + meeting.time_zone)} </span>
              </div>
            </div>
            
            {/* Countdown (Hides when overdue like Flutter) */}
            {!isOverdue && (
              <div className="text-end d-flex flex-column justify-content-center flex-shrink-0">
                <span style={{ fontSize: "9px", color: kTextDim, fontWeight: "500", letterSpacing: "0.2px", marginBottom: "2px" }}>Starts in</span>
                <span style={{ fontSize: "18px", fontWeight: "900", color: urgencyColor, letterSpacing: "-0.5px", whiteSpace: "nowrap", lineHeight: 1 }}>
                  {formatCountdownText()}
                </span>
              </div>
            )}
          </div>

           {/* Agenda preview (if any) */}
           {meeting.note && (
              <div className="d-flex align-items-start mt-2 mb-3">
                <i className="fa-solid fa-list mt-1 me-2" style={{ fontSize: "11px", color: kTextDim }}></i>
                <span className="text-truncate d-inline-block" style={{ fontSize: "12px", color: "#4B5563", maxWidth: "100%" }}>
                  {meeting.note.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ')}
                </span>
              </div>
           )}

          {/* Bottom Action Row (Avatars / RSVP + Join Button) */}
          <div className="d-flex justify-content-between align-items-center mt-3 pt-1">
            
            {/* If host, show invited users overlapping */}
            {isOwner ? (
                <div className="d-flex align-items-center">
                  <div className="d-flex">
                    {meeting.users && meeting.users.slice(0, 4).map((u, i) => (
                      <div 
                        key={u.user_id} 
                        className="rounded-circle border border-2 border-white bg-light d-flex align-items-center justify-content-center"
                        style={{ width: "26px", height: "26px", marginLeft: i === 0 ? "0" : "-8px", zIndex: 10 - i, overflow: 'hidden' }}
                        title={u.name}
                      >
                        {u.avatar && u.avatar.includes("http") ? (
                           <img src={u.avatar} alt="u" style={{width: "100%", height: "100%", objectFit: "cover"}} />
                        ) : (
                           <span style={{ fontSize: "10px", color: kTextDim }}>{u.name?.charAt(0)}</span>
                        )}
                      </div>
                    ))}
                    {meeting.users && meeting.users.length > 4 && (
                      <div className="rounded-circle border border-2 border-white d-flex align-items-center justify-content-center" style={{ width: "26px", height: "26px", marginLeft: "-8px", zIndex: 5, backgroundColor: "#E5E7EB" }}>
                        <span style={{ fontSize: "9px", fontWeight: "600", color: kTextDim }}>+{meeting.users.length - 4}</span>
                      </div>
                    )}
                  </div>
                </div>
            ) : (
              // If invitee, show the newly designed compact inline RSVP editor!
              formatRsvpBadge()
            )}

            {/* Action Button */}
            <div className="ms-auto flex-shrink-0 no-card-click">
              {isOverdue || isOwner ? (
                <button 
                  onClick={() => onStart(meeting.room_name)}
                  className="btn border-0 shadow-sm"
                  style={{
                    background: "linear-gradient(to right, #10B981, #059669)",
                    color: "white",
                    borderRadius: "40px",
                    padding: "8px 16px",
                    fontWeight: "600",
                    fontSize: "13px"
                  }}
                >
                  <i className="fa-solid fa-play me-2"></i>
                  {isOwner ? "Start Now" : "Join Now"}
                </button>
              ) : (
                <button 
                  onClick={() => window.location.href = `/meeting-waiting/${meeting.room_name}`}
                  className="btn border-0 shadow-sm"
                  style={{
                    background: "linear-gradient(to right, #F59E0B, #EA580C)",
                    color: "white",
                    borderRadius: "40px",
                    padding: "8px 16px",
                    fontWeight: "600",
                    fontSize: "13px"
                  }}
                >
                  <i className="fa-regular fa-calendar me-2"></i>
                  View Details
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
